import { PartShape, OffcutShape } from './PieceShapes';
import type { EdgeAssignment, GrooveAssignment } from '../model/types';

/**
 * Block 14 «Раскрой» — одна этикетка 60×40 мм (ландшафт), презентационный компонент.
 *
 * Содержит: рисунок детали/остатка (через `PartShape`/`OffcutShape` — тот же рендер,
 * что на карте), номер (№ детали / О№ остатка), название проекта, материал и в
 * левом верхнем углу стрелку ↖ (ориентация для кромкооблицовки). QR нет; размеры/
 * имя/кромки/пазы отдельным текстом НЕ дублируются — они уже в рисунке.
 *
 * ⚠️ Цвета подписей/рамки заданы ИНЛАЙН `#000000`/`#ffffff` (а не токенами палитры
 * P-045): SVG этикетки в следующем PR растеризуется в PDF через `svgToPng`, где
 * `cutting-theme` (CSS-переменные) недоступен — это тот же принятый паттерн, что в
 * `PartShape` (детали/кромки), и он не нарушает P-045.
 */
export interface LabelCardProps {
  kind: 'part' | 'offcut';
  w: number; // мм (деталь — как размещена; остаток — длина)
  h: number; // мм (деталь — как размещена; остаток — ширина)
  num: number; // № детали / номер остатка
  name?: string; // имя детали (для остатка не используется)
  edges?: EdgeAssignment;
  grooves?: GrooveAssignment;
  projectName: string;
  material: string;
}

export function LabelCard({
  kind,
  w,
  h,
  num,
  name,
  edges,
  grooves,
  projectName,
  material,
}: LabelCardProps) {
  const numText = kind === 'part' ? `№${num}` : `О${num}`;

  // Бокс рисунка внутри этикетки + масштаб детали/остатка в этот бокс.
  const x0 = 2;
  const y0 = 11;
  const boxW = 56;
  const boxH = 27;
  const s = Math.min(boxW / w, boxH / h);
  const tx = x0 + (boxW - w * s) / 2;
  const ty = y0 + (boxH - h * s) / 2;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 60 40"
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: 'auto', display: 'block' }}
      role="img"
      aria-label={`Этикетка ${kind === 'part' ? '№' + num : 'О' + num}`}
    >
      <rect
        x={0.3}
        y={0.3}
        width={59.4}
        height={39.4}
        fill="#ffffff"
        stroke="#000000"
        strokeWidth={0.4}
      />
      {/* Стрелка ↖ (остриё к углу 0,0) — ориентация для кромкооблицовки. */}
      <line x1={2} y1={2} x2={8} y2={8} stroke="#000000" strokeWidth={0.6} />
      <line x1={2} y1={2} x2={6} y2={2} stroke="#000000" strokeWidth={0.6} />
      <line x1={2} y1={2} x2={2} y2={6} stroke="#000000" strokeWidth={0.6} />
      <text x={57.5} y={6} textAnchor="end" fontSize={5} fill="#000000">
        {numText}
      </text>
      <text x={30} y={4.5} textAnchor="middle" fontSize={3} fill="#000000">
        {projectName}
      </text>
      <text x={30} y={8.5} textAnchor="middle" fontSize={2.7} fill="#000000">
        {material}
      </text>
      <g transform={`translate(${tx},${ty}) scale(${s})`}>
        {kind === 'part' ? (
          <PartShape w={w} h={h} name={name ?? ''} num={num} edges={edges} grooves={grooves} />
        ) : (
          <OffcutShape w={w} h={h} num={num} />
        )}
      </g>
    </svg>
  );
}
